import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { lazy, Suspense } from 'preact/compat';
import { Redirect, Route, Router, Switch, useLocation } from 'wouter-preact';
import { AppShell } from '@/components/navigation/AppShell';
import { OnboardingGate } from '@/components/navigation/OnboardingGate';
import { Toaster } from '@/components/feedback/Toaster';
import { ConfirmHost } from '@/components/overlays/ConfirmDialog';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { authStatus, bootstrapAuth, isAuthed, isLoadingAuth } from '@/stores/auth';
import { setCurrentPath } from '@/stores/navigation';
import { useHotkey } from '@/hooks/useHotkey';
import { isPaletteOpen, togglePalette } from '@/stores/commandPalette';
import { bootCommandPalette } from '@/lib/commandPalette';

// CommandPalette pesa ~20KB (Fuse.js + listings + actions). Lazy: solo se
// carga la primera vez que el usuario pulsa mod+k. El hotkey de apertura
// vive abajo en el componente, asi sigue funcionando aunque el chunk no
// haya cargado todavia.
const CommandPalette = lazy(() =>
  import('@/components/navigation/CommandPalette').then((m) => ({ default: m.CommandPalette })),
);

// ───── Eager ──────────────────────────────────────────────────────────────
// Solo PlaceholderPage: es el fallback de rutas (404) y debe renderizar sin
// esperar un chunk lazy.
import { PlaceholderPage } from '@/pages/core/PlaceholderPage';

// CuentaPage NO es la primera vista tras login (es /cuenta). Eager arrastraba
// zod + el sistema de forms (~27KB gz solo de zod) al chunk crítico que se baja
// en TODA visita, incluido el login no autenticado. Lazy + prefetch en idle
// (WORKSPACE_PREFETCH) la mantiene instantánea sin penalizar el first paint.
const CuentaPage = lazy(() =>
  import('@/pages/core/CuentaPage').then((m) => ({ default: m.CuentaPage })),
);

// ───── Auth chunk ────────────────────────────────────────────────────────
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('@/pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const ConfirmEmailChangePage = lazy(() =>
  import('@/pages/auth/ConfirmEmailChangePage').then((m) => ({
    default: m.ConfirmEmailChangePage,
  })),
);

// ───── Workspace chunks ──────────────────────────────────────────────────
const LeadsListPage = lazy(() =>
  import('@/pages/leads/LeadsListPage').then((m) => ({ default: m.LeadsListPage })),
);

// ───── Biblioteca chunk ──────────────────────────────────────────────────
const CampanasPage = lazy(() =>
  import('@/pages/biblioteca/CampanasPage').then((m) => ({ default: m.CampanasPage })),
);
const AdsetsPage = lazy(() =>
  import('@/pages/biblioteca/AdsetsPage').then((m) => ({ default: m.AdsetsPage })),
);
const AdsPage = lazy(() =>
  import('@/pages/biblioteca/AdsPage').then((m) => ({ default: m.AdsPage })),
);

// ───── Economía chunk ────────────────────────────────────────────────────
// Rediseño Rentabilidad 2026-06-05: 11 páginas → 4 (Visión general,
// Por producto, Marketing, Configuración) + Setup. Rutas viejas redirigen.
const VisionGeneralPage = lazy(() =>
  import('@/pages/rentabilidad/VisionGeneralPage').then((m) => ({ default: m.VisionGeneralPage })),
);
const EconomiaSetupPage = lazy(() =>
  import('@/pages/rentabilidad/SetupPage').then((m) => ({ default: m.SetupPage })),
);
const ConfiguracionPage = lazy(() =>
  import('@/pages/rentabilidad/ConfiguracionPage').then((m) => ({ default: m.ConfiguracionPage })),
);
const PorProductoPage = lazy(() =>
  import('@/pages/rentabilidad/PorProductoPage').then((m) => ({ default: m.PorProductoPage })),
);
const MarketingPage = lazy(() =>
  import('@/pages/rentabilidad/MarketingPage').then((m) => ({ default: m.MarketingPage })),
);

// ───── Método QQ chunk ──────────────────────────────────────────────────
const RecommendedActionsPage = lazy(() =>
  import('@/pages/recommended-actions/RecommendedActionsPage').then((m) => ({
    default: m.RecommendedActionsPage,
  })),
);

// ───── Trace Pilot chunk ────────────────────────────────────────────────
const TracePilotDashboardPage = lazy(() =>
  import('@/pages/trace-pilot/TracePilotDashboardPage').then((m) => ({
    default: m.TracePilotDashboardPage,
  })),
);
const TraceOrderDetailPage = lazy(() =>
  import('@/pages/trace-pilot/TraceOrderDetailPage').then((m) => ({
    default: m.TraceOrderDetailPage,
  })),
);
const TraceAdSummaryPage = lazy(() =>
  import('@/pages/trace-pilot/TraceAdSummaryPage').then((m) => ({
    default: m.TraceAdSummaryPage,
  })),
);
const TraceUrlTagsAuditPage = lazy(() =>
  import('@/pages/trace-pilot/TraceUrlTagsAuditPage').then((m) => ({
    default: m.TraceUrlTagsAuditPage,
  })),
);

// ───── Atribución chunk ──────────────────────────────────────────────────
const AtribucionOverviewPage = lazy(() =>
  import('@/pages/atribucion/AtribucionPages').then((m) => ({ default: m.AtribucionOverviewPage })),
);
const AtribucionOrphansPage = lazy(() =>
  import('@/pages/atribucion/AtribucionPages').then((m) => ({ default: m.AtribucionOrphansPage })),
);
const TrackingHealthPage = lazy(() =>
  import('@/pages/atribucion/TrackingHealthPage').then((m) => ({ default: m.TrackingHealthPage })),
);
const DedupeSuggestionsPage = lazy(() =>
  import('@/pages/atribucion/DedupeSuggestionsPage').then((m) => ({
    default: m.DedupeSuggestionsPage,
  })),
);
const TrackingReviewPage = lazy(() =>
  import('@/pages/atribucion/TrackingReviewPage').then((m) => ({ default: m.TrackingReviewPage })),
);

// ───── Contabilidad chunk ────────────────────────────────────────────────
const ShopifyOrdersPage = lazy(() =>
  import('@/pages/contabilidad/OrdersWorkspace').then((m) => ({ default: m.ShopifyOrdersPage })),
);
const ShopifyCustomersPage = lazy(() =>
  import('@/pages/clientes/ShopifyCustomersPage').then((m) => ({
    default: m.ShopifyCustomersPage,
  })),
);
const FichaClientePage = lazy(() =>
  import('@/pages/clientes/ficha/FichaClientePage').then((m) => ({
    default: m.FichaClientePage,
  })),
);
const MetaTransaccionesPage = lazy(() =>
  import('@/pages/transacciones/MetaTransaccionesPage').then((m) => ({
    default: m.MetaTransaccionesPage,
  })),
);

// ───── Reporting chunk ───────────────────────────────────────────────────
const SalesProfitabilityDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/SalesProfitabilityDashboardPage').then((m) => ({
    default: m.SalesProfitabilityDashboardPage,
  })),
);
const LaboratorioPage = lazy(() =>
  import('@/pages/dashboard-v2/LaboratorioPage').then((m) => ({ default: m.LaboratorioPage })),
);
const MetaAdsDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/MetaAdsDashboardPage').then((m) => ({
    default: m.MetaAdsDashboardPage,
  })),
);
const CreativesHubPage = lazy(() =>
  import('@/pages/dashboard-v2/CreativesHubPage').then((m) => ({
    default: m.CreativesHubPage,
  })),
);
const WebHubPage = lazy(() =>
  import('@/pages/dashboard-v2/WebHubPage').then((m) => ({
    default: m.WebHubPage,
  })),
);
const CustomersHubPage = lazy(() =>
  import('@/pages/dashboard-v2/CustomersHubPage').then((m) => ({
    default: m.CustomersHubPage,
  })),
);
const CustomerLtvDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/CustomerLtvDashboardPage').then((m) => ({
    default: m.CustomerLtvDashboardPage,
  })),
);
const KlaviyoDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/KlaviyoDashboardPage').then((m) => ({
    default: m.KlaviyoDashboardPage,
  })),
);
const LandingsFunnelDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/WebDetailPages').then((m) => ({
    default: m.LandingsFunnelDashboardPage,
  })),
);
const ClarityBehaviorDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/WebDetailPages').then((m) => ({
    default: m.ClarityBehaviorDashboardPage,
  })),
);
const ProductOffersDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/ProductOffersDashboardPage').then((m) => ({
    default: m.ProductOffersDashboardPage,
  })),
);
const CreativeIntelligenceDashboardPage = lazy(() =>
  import('@/pages/dashboard-v2/CreativeIntelligenceDashboardPage').then((m) => ({
    default: m.CreativeIntelligenceDashboardPage,
  })),
);
const MarketingControlPage = lazy(() =>
  import('@/pages/control/MarketingControlPage').then((m) => ({ default: m.MarketingControlPage })),
);
const AssistantReviewPage = lazy(() =>
  import('@/pages/reporting/AssistantReviewPage').then((m) => ({ default: m.AssistantReviewPage })),
);
const WeeklyReviewPage = lazy(() =>
  import('@/pages/reporting/WeeklyReviewPage').then((m) => ({ default: m.WeeklyReviewPage })),
);
const MarketingIntelligencePage = lazy(() =>
  import('@/pages/reporting/MarketingIntelligencePage').then((m) => ({
    default: m.MarketingIntelligencePage,
  })),
);
const CoreMethodPage = lazy(() =>
  import('@/pages/reporting/ReportingPages').then((m) => ({ default: m.CoreMethodPage })),
);
const CoreEconomicPage = lazy(() =>
  import('@/pages/reporting/ReportingPages').then((m) => ({ default: m.CoreEconomicPage })),
);
const FunnelPage = lazy(() =>
  import('@/pages/reporting/ReportingPages').then((m) => ({ default: m.FunnelPage })),
);
const DirectorsPage = lazy(() =>
  import('@/pages/reporting/ReportingPages').then((m) => ({ default: m.DirectorsPage })),
);
const HooksPage = lazy(() =>
  import('@/pages/reporting/ReportingPages').then((m) => ({ default: m.HooksPage })),
);
// Plan 2026-05-18 Fase 10 (transcript líneas 211-212): adquisición
// histórica por creativa.
const HistoricalAcquisitionPage = lazy(() =>
  import('@/pages/reporting/ReportingPages').then((m) => ({
    default: m.HistoricalAcquisitionPage,
  })),
);
// Plan 2026-05-18 Fase 6.6: Product Mix eliminado — la rentabilidad por SKU
// vive en Economía (transcript líneas 239-243).

// ───── Configuración chunk ───────────────────────────────────────────────
const ResumenPage = lazy(() =>
  import('@/pages/configuracion/ResumenPage').then((m) => ({ default: m.ResumenPage })),
);
const OnboardingPage = lazy(() =>
  import('@/pages/configuracion/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const UsuariosPage = lazy(() =>
  import('@/pages/configuracion/UsuariosPage').then((m) => ({ default: m.UsuariosPage })),
);
const EmpresasPage = lazy(() =>
  import('@/pages/configuracion/EmpresasPage').then((m) => ({ default: m.EmpresasPage })),
);
const ReglasPage = lazy(() =>
  import('@/pages/configuracion/ReglasPage').then((m) => ({ default: m.ReglasPage })),
);
const CamposPage = lazy(() =>
  import('@/pages/configuracion/CamposPage').then((m) => ({ default: m.CamposPage })),
);
const CuponesInternosPage = lazy(() =>
  import('@/pages/configuracion/CuponesInternosPage').then((m) => ({
    default: m.CuponesInternosPage,
  })),
);
const IntegracionesPage = lazy(() =>
  import('@/pages/configuracion/IntegracionesPage').then((m) => ({ default: m.IntegracionesPage })),
);
const SincronizacionesPage = lazy(() =>
  import('@/pages/configuracion/SincronizacionesPage').then((m) => ({
    default: m.SincronizacionesPage,
  })),
);
const IntegrationDetailPage = lazy(() =>
  import('@/pages/configuracion/IntegrationDetailPage').then((m) => ({
    default: m.IntegrationDetailPage,
  })),
);
const AuditPage = lazy(() =>
  import('@/pages/configuracion/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const ObjetivosPage = lazy(() =>
  import('@/pages/configuracion/ObjetivosPage').then((m) => ({ default: m.ObjetivosPage })),
);
const CarteraPage = lazy(() =>
  import('@/pages/configuracion/CarteraPage').then((m) => ({ default: m.CarteraPage })),
);
const SeguridadPage = lazy(() =>
  import('@/pages/configuracion/SeguridadPage').then((m) => ({ default: m.SeguridadPage })),
);

// ───── Legal chunk (URLs públicas requeridas por Meta App Review) ────────
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/legal/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsPage = lazy(() =>
  import('@/pages/legal/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const DataDeletionPage = lazy(() =>
  import('@/pages/legal/DataDeletionPage').then((m) => ({ default: m.DataDeletionPage })),
);

// Redirect que conserva el resto del path al renombrar un prefijo de dominio
// (Fase 4 rediseño nav): /economia/* → /rentabilidad/*, /contabilidad/* → /pedidos.
// Lee la location actual en vez del param para no depender del nombre del
// wildcard de wouter.
function PrefixRedirect({ from, to }: { from: string; to: string }) {
  const [path] = useLocation();
  const tail = path.startsWith(from) ? path.slice(from.length) : '';
  return <Redirect to={`${to}${tail}`} />;
}

const PUBLIC_ROUTES = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
]);

function isPublicPath(path: string): boolean {
  return PUBLIC_ROUTES.has(path) || path.startsWith('/auth/');
}

// Rutas accesibles para todo el mundo (authed o no) sin redirección. A
// diferencia de las auth pages, no queremos kickear al usuario logueado
// fuera de /legal/* — necesita poder consultar la política de privacidad
// sin que AuthGate lo mande a /.
function isOpenPath(path: string): boolean {
  return path.startsWith('/legal/');
}

function PathTracker() {
  const [path] = useLocation();
  useEffect(() => {
    setCurrentPath(path);
  }, [path]);
  return null;
}

function AuthGate() {
  const [path, navigate] = useLocation();
  const authed = isAuthed.value;
  const loading = isLoadingAuth.value;

  useEffect(() => {
    if (loading) return;
    if (isOpenPath(path)) return;
    if (!authed && !isPublicPath(path)) {
      navigate('/auth/login', { replace: true });
    } else if (authed && isPublicPath(path)) {
      navigate('/', { replace: true });
    }
  }, [authed, loading, path, navigate]);

  return null;
}

/**
 * Suspense fallback that delays rendering the spinner so fast chunks don't flash.
 * If the chunk resolves before `delay` ms, nothing visible appears.
 */
function PageFallback({ delay = 150 }: { delay?: number }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShow(true), delay);
    return () => window.clearTimeout(t);
  }, [delay]);
  if (!show) return null;
  return (
    <div
      role="status"
      aria-label="Cargando página"
      aria-live="polite"
      aria-busy="true"
      className="text-fg-muted animate-fade-in flex min-h-[40vh] items-center justify-center"
    >
      <Spinner size={22} />
    </div>
  );
}

/**
 * Re-keys its child on every path change so the wrapper re-mounts and the
 * `animate-route-enter` animation re-plays. Lightweight: the inner Switch
 * already re-renders on path change — this only adds the animation envelope.
 */
// Prefetch de chunks hermanos por dominio: al entrar en un destino con varias
// pestañas (chunks lazy distintos), cargamos en segundo plano (idle) el resto
// de sus páginas para que cambiar de tab sea instantáneo, sin parpadeo de
// Suspense. Scoped al dominio actual + en idle → no compite con el render ni
// dispara el consumo. (Informes y la mayoría de Configuración comparten un
// único chunk, así que no necesitan prefetch.)
const SECTION_PREFETCH: Record<string, Array<() => Promise<unknown>>> = {
  '/atribucion': [
    () => import('@/pages/atribucion/AtribucionPages'),
    () => import('@/pages/atribucion/TrackingHealthPage'),
    () => import('@/pages/atribucion/DedupeSuggestionsPage'),
    () => import('@/pages/atribucion/TrackingReviewPage'),
    () => import('@/pages/trace-pilot/TracePilotDashboardPage'),
    () => import('@/pages/trace-pilot/TraceUrlTagsAuditPage'),
  ],
  '/clientes': [
    () => import('@/pages/leads/LeadsListPage'),
    () => import('@/pages/clientes/ShopifyCustomersPage'),
  ],
  '/biblioteca': [
    () => import('@/pages/biblioteca/CampanasPage'),
    () => import('@/pages/biblioteca/AdsetsPage'),
    () => import('@/pages/biblioteca/AdsPage'),
    () => import('@/pages/transacciones/MetaTransaccionesPage'),
  ],
  '/rentabilidad': [
    () => import('@/pages/rentabilidad/VisionGeneralPage'),
    () => import('@/pages/rentabilidad/ConfiguracionPage'),
    () => import('@/pages/rentabilidad/PorProductoPage'),
    () => import('@/pages/rentabilidad/MarketingPage'),
    () => import('@/pages/rentabilidad/SetupPage'),
  ],
};

function usePrefetchSiblings(): void {
  const [path] = useLocation();
  const section = '/' + (path.split('/')[1] ?? '');
  useEffect(() => {
    const thunks = SECTION_PREFETCH[section];
    if (!thunks) return;
    const run = () => {
      for (const load of thunks) void load().catch(() => {});
    };
    const ric = window.requestIdleCallback;
    if (typeof ric === 'function') {
      const id = ric(run);
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(run, 400);
    return () => clearTimeout(id);
  }, [section]);
}

// Chunk de ATERRIZAJE de cada destino del sidebar (lo que se ve al pulsarlo).
// Control de marketing (landing post-login, '/') va PRIMERO para minimizar el
// Suspense tras login. Mi cuenta (CuentaPage) también lazy aquí. Ambas fuera del
// chunk crítico que se baja en /login.
const WORKSPACE_PREFETCH: Array<() => Promise<unknown>> = [
  () => import('@/pages/control/MarketingControlPage'), // Control de marketing → '/'
  () => import('@/pages/leads/LeadsListPage'), // Clientes → /clientes/leads
  () => import('@/pages/contabilidad/OrdersWorkspace'), // Pedidos
  () => import('@/pages/biblioteca/CampanasPage'), // Marketing → /biblioteca/campanas
  () => import('@/pages/atribucion/AtribucionPages'), // Atribución
  () => import('@/pages/rentabilidad/VisionGeneralPage'), // Rentabilidad
  () => import('@/pages/reporting/MarketingIntelligencePage'), // Informes → /reporting/inteligencia
  () => import('@/pages/configuracion/ResumenPage'), // Configuración
  () => import('@/pages/core/CuentaPage'), // Mi cuenta
];

// Tras el primer render autenticado, en idle y ESCALONADO (una carga por
// idle-tick), precargamos el landing de cada sección. Así la primera visita a
// cualquier destino no muestra el parpadeo de Suspense del chunk lazy. El
// escalonado evita encolar 7 requests de golpe → no dispara el consumo. Las
// pestañas hermanas de cada dominio siguen cargándose on-demand (siblings).
function usePrefetchWorkspace(): void {
  const authed = isAuthed.value;
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    const ric = window.requestIdleCallback;
    const pending = [...WORKSPACE_PREFETCH];
    let scheduled: number;
    const step = () => {
      if (cancelled) return;
      const load = pending.shift();
      if (!load) return;
      void load().catch(() => {});
      scheduled =
        typeof ric === 'function' ? ric(step) : (setTimeout(step, 250) as unknown as number);
    };
    scheduled =
      typeof ric === 'function' ? ric(step) : (setTimeout(step, 600) as unknown as number);
    return () => {
      cancelled = true;
      if (typeof ric === 'function') window.cancelIdleCallback(scheduled);
      else clearTimeout(scheduled);
    };
  }, [authed]);
}

function RouteTransition({ children }: { children: ComponentChildren }) {
  const [path] = useLocation();
  // `key={path}` re-monta el contenido en cada navegación → swap atómico, sin
  // residuo de la página anterior. `route-fade` reproduce un fade-in de
  // opacidad al montar (ver globals.css): transición limpia, sin overlay ni
  // solape de dos páginas. Respeta prefers-reduced-motion.
  return (
    <div key={path} className="route-fade">
      {children}
    </div>
  );
}

export function App() {
  const status = authStatus.value;
  const authed = isAuthed.value;
  const paletteOpen = isPaletteOpen.value;

  // Trigger bootstrap on first mount, regardless of which branch we render below.
  useEffect(() => {
    if (authStatus.value === 'idle') void bootstrapAuth();
  }, []);

  // mod+k eager: el hotkey debe estar registrado aunque CommandPalette (lazy)
  // no haya cargado. Pulsar mod+k flippea isPaletteOpen → React monta el
  // CommandPalette (Suspense) y al terminar el dynamic import se muestra.
  useHotkey('mod+k', () => togglePalette(), { allowInEditable: true });
  useEffect(() => bootCommandPalette(), []);

  // Calienta los chunks de las pestañas hermanas del dominio actual (idle).
  usePrefetchSiblings();
  // Precarga el landing de las 7 secciones tras el primer render (idle).
  usePrefetchWorkspace();

  if (status === 'idle' || status === 'loading') {
    return (
      <div
        role="status"
        aria-label="Cargando aplicación"
        aria-busy="true"
        className="bg-app text-fg-muted flex min-h-screen items-center justify-center"
      >
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <>
      <Router>
        <PathTracker />
        <AuthGate />
        <Suspense fallback={<PageFallback />}>
          <Switch>
            <Route path="/auth/login" component={LoginPage} />
            <Route path="/auth/register" component={RegisterPage} />
            <Route path="/auth/forgot-password" component={ForgotPasswordPage} />
            <Route path="/auth/reset-password" component={ResetPasswordPage} />
            <Route path="/auth/verify-email" component={VerifyEmailPage} />
            <Route path="/auth/confirm-email-change" component={ConfirmEmailChangePage} />

            <Route path="/legal/privacy" component={PrivacyPolicyPage} />
            <Route path="/legal/terms" component={TermsPage} />
            <Route path="/legal/data-deletion" component={DataDeletionPage} />

            {authed ? (
              <Route>
                <AppShell>
                  <OnboardingGate>
                    <ErrorBoundary>
                      <Suspense fallback={<PageFallback />}>
                        <RouteTransition>
                          <Switch>
                            <Route path="/" component={MarketingControlPage} />
                            <Route path="/resumen-anterior">
                              <Redirect to="/" />
                            </Route>

                            {/* Clientes y repetición: diagnóstico primero; las
                              vistas operativas siguen en sus rutas propias. */}
                            <Route path="/clientes" component={CustomersHubPage} />
                            <Route path="/clientes/ltv" component={CustomerLtvDashboardPage} />
                            <Route path="/clientes/klaviyo" component={KlaviyoDashboardPage} />
                            <Route path="/clientes/leads" component={LeadsListPage} />
                            <Route path="/clientes/lista" component={ShopifyCustomersPage} />
                            <Route path="/clientes/ficha/:id" component={FichaClientePage} />
                            {/* Cohortes vive dentro de Valor y LTV (estructura fase 2). */}
                            <Route path="/clientes/cohortes">
                              <Redirect to="/clientes/ltv" />
                            </Route>
                            {/* Redirects legacy → hub Clientes. */}
                            <Route path="/leads">
                              <Redirect to="/clientes/leads" />
                            </Route>

                            <Route path="/biblioteca">
                              <Redirect to="/biblioteca/campanas" />
                            </Route>
                            <Route path="/biblioteca/campanas" component={CampanasPage} />
                            <Route path="/biblioteca/adsets" component={AdsetsPage} />
                            <Route path="/biblioteca/ads" component={AdsPage} />
                            {/* Gasto Meta: re-homeado bajo Marketing (antes
                              /transacciones/meta, huérfano tras el rediseño). */}
                            <Route path="/biblioteca/gasto" component={MetaTransaccionesPage} />

                            <Route path="/rentabilidad" component={VisionGeneralPage} />
                            <Route path="/rentabilidad/setup" component={EconomiaSetupPage} />
                            <Route path="/rentabilidad/productos" component={PorProductoPage} />
                            <Route path="/rentabilidad/marketing" component={MarketingPage} />
                            <Route
                              path="/rentabilidad/configuracion"
                              component={ConfiguracionPage}
                            />
                            {/* Redirects del rediseño 2026-06-05: lectura
                              consolidada en Visión general, edición en
                              Configuración, SKU como tab de Por producto. */}
                            <Route path="/rentabilidad/perfil">
                              <Redirect to="/rentabilidad/configuracion" />
                            </Route>
                            <Route path="/rentabilidad/escenarios">
                              <Redirect to="/rentabilidad/configuracion" />
                            </Route>
                            <Route path="/rentabilidad/viabilidad">
                              <Redirect to="/rentabilidad" />
                            </Route>
                            <Route path="/rentabilidad/cac">
                              <Redirect to="/rentabilidad" />
                            </Route>
                            <Route path="/rentabilidad/ltgp">
                              <Redirect to="/rentabilidad" />
                            </Route>
                            <Route path="/rentabilidad/runway">
                              <Redirect to="/rentabilidad" />
                            </Route>
                            <Route path="/rentabilidad/profitabilidad">
                              <Redirect to="/rentabilidad/productos?tab=rentabilidad" />
                            </Route>
                            <Route path="/rentabilidad/cohortes">
                              <Redirect to="/clientes/ltv" />
                            </Route>
                            {/* Redirects legacy /economia/* → /rentabilidad/* (Fase 4). */}
                            <Route path="/economia/:rest*">
                              <PrefixRedirect from="/economia" to="/rentabilidad" />
                            </Route>
                            <Route path="/economia">
                              <Redirect to="/rentabilidad" />
                            </Route>

                            <Route path="/atribucion" component={AtribucionOverviewPage} />
                            <Route path="/atribucion/salud" component={TrackingHealthPage} />
                            <Route path="/atribucion/huerfanos" component={AtribucionOrphansPage} />
                            <Route path="/atribucion/dedupe" component={DedupeSuggestionsPage} />
                            <Route path="/atribucion/revision" component={TrackingReviewPage} />
                            {/* Redirects legacy de slugs renombrados (Fase 5). */}
                            <Route path="/atribucion/tracking-health">
                              <Redirect to="/atribucion/salud" />
                            </Route>
                            <Route path="/atribucion/dedupe-suggestions">
                              <Redirect to="/atribucion/dedupe" />
                            </Route>
                            <Route path="/atribucion/tracking-review">
                              <Redirect to="/atribucion/revision" />
                            </Route>
                            {/* Trazabilidad: antiguo "Trazado piloto", absorbido
                              en Atribución (Fase 2 rediseño nav). */}
                            <Route path="/atribucion/trazas" component={TracePilotDashboardPage} />
                            <Route path="/atribucion/url-tags" component={TraceUrlTagsAuditPage} />
                            <Route
                              path="/atribucion/orders/:orderId"
                              component={TraceOrderDetailPage}
                            />
                            <Route path="/atribucion/ads/:adId" component={TraceAdSummaryPage} />

                            {/* Pedidos: antiguo /contabilidad (Fase 4 rediseño nav). */}
                            <Route path="/pedidos" component={ShopifyOrdersPage} />
                            {/* Redirects legacy /contabilidad/* → destinos nuevos. */}
                            <Route path="/contabilidad/shopify-clientes">
                              <Redirect to="/clientes/lista" />
                            </Route>
                            <Route path="/contabilidad/shopify-ordenes">
                              <Redirect to="/pedidos" />
                            </Route>
                            <Route path="/contabilidad">
                              <Redirect to="/pedidos" />
                            </Route>
                            <Route path="/transacciones/meta">
                              <Redirect to="/biblioteca/gasto" />
                            </Route>

                            <Route
                              path="/dashboard-v2/ventas"
                              component={SalesProfitabilityDashboardPage}
                            />
                            <Route path="/dashboard-v2/meta-ads">
                              <Redirect to="/creativos/meta-ads" />
                            </Route>
                            <Route path="/dashboard-v2/creative-intelligence">
                              <Redirect to="/creativos/creative-intelligence" />
                            </Route>
                            <Route path="/creativos" component={CreativesHubPage} />
                            <Route path="/web" component={WebHubPage} />
                            <Route path="/web/funnel" component={LandingsFunnelDashboardPage} />
                            <Route
                              path="/web/comportamiento"
                              component={ClarityBehaviorDashboardPage}
                            />
                            <Route
                              path="/web/productos-ofertas"
                              component={ProductOffersDashboardPage}
                            />
                            <Route path="/creativos/meta-ads" component={MetaAdsDashboardPage} />
                            <Route path="/creativos/laboratorio" component={LaboratorioPage} />
                            <Route
                              path="/creativos/creative-intelligence"
                              component={CreativeIntelligenceDashboardPage}
                            />
                            <Route path="/control" component={MarketingControlPage} />
                            {/* Dashboard semanal de referencia retirado (2026-09-18):
                              pesaba 2,5 MB en el chunk de Reporting y llevaba datos
                              de 101. Las URLs antiguas llevan al Control. */}
                            <Route path="/dashboard-v2">
                              <Redirect to="/" />
                            </Route>
                            <Route path="/dashboard-v2/landings-funnel">
                              <Redirect to="/web/funnel" />
                            </Route>
                            <Route path="/dashboard-v2/clarity">
                              <Redirect to="/web/comportamiento" />
                            </Route>
                            <Route path="/dashboard-v2/productos-ofertas">
                              <Redirect to="/web/productos-ofertas" />
                            </Route>
                            <Route path="/dashboard-v2/clientes-ltv">
                              <Redirect to="/clientes/ltv" />
                            </Route>
                            <Route path="/dashboard-v2/klaviyo">
                              <Redirect to="/clientes/klaviyo" />
                            </Route>
                            <Route path="/dashboard-v2/:section">
                              <Redirect to="/" />
                            </Route>
                            <Route path="/dapoa-v2">
                              <Redirect to="/" />
                            </Route>

                            <Route path="/reporting">
                              <Redirect to="/" />
                            </Route>
                            <Route
                              path="/reporting/inteligencia"
                              component={MarketingIntelligencePage}
                            />
                            <Route
                              path="/reporting/validacion-asistente"
                              component={AssistantReviewPage}
                            />
                            <Route path="/reporting/semanal" component={WeeklyReviewPage} />
                            <Route path="/reporting/core-method" component={CoreMethodPage} />
                            <Route path="/reporting/core-economic" component={CoreEconomicPage} />
                            <Route path="/reporting/funnel" component={FunnelPage} />
                            <Route path="/reporting/directors" component={DirectorsPage} />
                            <Route path="/reporting/hooks" component={HooksPage} />
                            <Route
                              path="/reporting/historical-acquisition"
                              component={HistoricalAcquisitionPage}
                            />

                            <Route path="/configuracion" component={ResumenPage} />
                            <Route path="/configuracion/inicio" component={OnboardingPage} />
                            <Route path="/configuracion/usuarios" component={UsuariosPage} />
                            <Route path="/configuracion/empresas" component={EmpresasPage} />
                            <Route path="/configuracion/reglas" component={ReglasPage} />
                            <Route path="/configuracion/campos" component={CamposPage} />
                            <Route
                              path="/configuracion/cupones-internos"
                              component={CuponesInternosPage}
                            />
                            <Route
                              path="/configuracion/integraciones"
                              component={IntegracionesPage}
                            />
                            <Route
                              path="/configuracion/sincronizaciones"
                              component={SincronizacionesPage}
                            />
                            <Route path="/configuracion/integraciones/meta">
                              <IntegrationDetailPage provider="meta" />
                            </Route>
                            <Route path="/configuracion/integraciones/shopify">
                              <IntegrationDetailPage provider="shopify" />
                            </Route>
                            <Route path="/configuracion/integraciones/analytics">
                              <IntegrationDetailPage provider="google_analytics" />
                            </Route>
                            <Route path="/configuracion/integraciones/klaviyo">
                              <IntegrationDetailPage provider="klaviyo" />
                            </Route>
                            <Route path="/configuracion/integraciones/clarity">
                              <IntegrationDetailPage provider="clarity" />
                            </Route>
                            <Route path="/configuracion/integraciones/holded">
                              <IntegrationDetailPage provider="holded" />
                            </Route>
                            <Route path="/configuracion/audit" component={AuditPage} />
                            <Route path="/configuracion/objetivos" component={ObjetivosPage} />
                            <Route path="/configuracion/cartera" component={CarteraPage} />
                            <Route path="/configuracion/seguridad" component={SeguridadPage} />
                            <Route path="/recomendaciones" component={RecommendedActionsPage} />
                            {/* Redirects legacy /trace-pilot/* → /atribucion/*
                              (Fase 2 rediseño nav). Mantienen bookmarks vivos. */}
                            <Route path="/trace-pilot">
                              <Redirect to="/atribucion/trazas" />
                            </Route>
                            <Route path="/trace-pilot/url-tags">
                              <Redirect to="/atribucion/url-tags" />
                            </Route>
                            <Route path="/trace-pilot/orders/:orderId">
                              {(params) => <Redirect to={`/atribucion/orders/${params.orderId}`} />}
                            </Route>
                            <Route path="/trace-pilot/ads/:adId">
                              {(params) => <Redirect to={`/atribucion/ads/${params.adId}`} />}
                            </Route>
                            <Route path="/cuenta" component={CuentaPage} />
                            <Route>
                              <PlaceholderPage title="Página no encontrada" variant="not-found" />
                            </Route>
                          </Switch>
                        </RouteTransition>
                      </Suspense>
                    </ErrorBoundary>
                  </OnboardingGate>
                </AppShell>
              </Route>
            ) : (
              <Route>
                <Redirect to="/auth/login" />
              </Route>
            )}
          </Switch>
        </Suspense>
      </Router>
      <Toaster />
      <ConfirmHost />
      {paletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette />
        </Suspense>
      )}
    </>
  );
}
